import type {
  AcademicFetchResponse,
  AcademicSearchProvider,
  AcademicSearchPublicationField,
  AcademicSearchResponse,
  AskResponse,
  ParseResponse,
  TemporaryUploadResponse,
} from "./types.js";

// Re-export constants for convenience
export {
  AcademicSearchProviderValues,
  AcademicSearchPublicationFieldValues,
} from "./types.js";

// Re-export types for convenience
export type {
  AcademicFetchError,
  AcademicFetchResponse,
  AcademicFetchResult,
  AcademicSearchArxivData,
  AcademicSearchAuthor,
  AcademicSearchBasePublication,
  AcademicSearchEricData,
  AcademicSearchGoogleScholarData,
  AcademicSearchProvider,
  AcademicSearchProviderData,
  AcademicSearchProviderError,
  AcademicSearchPublication,
  AcademicSearchPublicationField,
  AcademicSearchPubMedData,
  AcademicSearchResponse,
  AcademicSearchSemanticScholarData,
  AskResponse,
  ParseResponse,
} from "./types.js";

/**
 * Base interface for parse request parameters
 */
export interface ParseBaseRequest {
  /** Whether to use LLM parsing. Defaults to "auto" */
  useLLM?: "auto" | "always" | "never";
}

/**
 * Request parameters for parsing a PDF/Word document from URL
 */
export interface ParseURLRequest extends ParseBaseRequest {
  /** Direct URL to a PDF/Word document that will be parsed and converted to markdown */
  url: string;
}

/**
 * Request parameters for parsing a PDF/Word document from data
 */
export interface ParseDataRequest extends ParseBaseRequest {
  /** Direct data of the document - supports string, Buffer, Uint8Array, ArrayBuffer, Blob, or ReadableStream */
  data: string | Buffer | Uint8Array | ArrayBuffer | Blob | ReadableStream;
  /** Content type of the document (e.g., "application/pdf", "application/msword") */
  contentType: string;
}

/**
 * Base interface for ask request parameters
 */
export interface AskBaseRequest {
  /** The question or prompt you want to ask about the document */
  prompt: string;
  /** Response format mode. Use 'markdown' for natural language responses or 'json' for structured data output. Defaults to 'markdown' */
  mode?: "markdown" | "json";
  /** JSON Schema object that defines the structure of the expected JSON output. Required when mode is 'json'. Should follow the JSON Schema specification */
  schema?: Record<string, unknown>;
}

/**
 * Request parameters for asking questions about a document from URL
 */
export interface AskURLRequest extends AskBaseRequest {
  /** Direct URL to a PDF/Word document that will be analyzed */
  url: string;
}

/**
 * Request parameters for asking questions about a document from data
 */
export interface AskDataRequest extends AskBaseRequest {
  /** Direct data of the document - supports string, Buffer, Uint8Array, ArrayBuffer, Blob, or ReadableStream */
  data: string | Buffer | Uint8Array | ArrayBuffer | Blob | ReadableStream;
  /** Content type of the document (e.g., "application/pdf", "application/msword") */
  contentType: string;
}

/**
 * Configuration for the PDF Vector client
 */
export interface PDFVectorConfig {
  /** API key for authentication (format: pdfvector_xxx) */
  apiKey: string;
  /** Base URL for the PDF Vector API. Defaults to https://www.pdfvector.com */
  baseUrl?: string;
}

/**
 * Request parameters for searching academic publications
 */
export interface SearchRequest {
  /** Search query string */
  query: string;
  /** Providers to search (defaults to ["semantic-scholar"]) */
  providers?: AcademicSearchProvider[];
  /** Number of results to skip (for pagination, defaults to 0) */
  offset?: number;
  /** Maximum number of results to return (1-100, defaults to 20) */
  limit?: number;
  /** Filter results by publication year (from) */
  yearFrom?: number;
  /** Filter results by publication year (to) */
  yearTo?: number;
  /** List of fields to include in the response. If not specified, all base publication fields are returned. Use 'providerData' to include provider-specific metadata. */
  fields?: AcademicSearchPublicationField[];
}

/**
 * Request parameters for fetching specific academic publications by ID
 */
export interface FetchRequest {
  /** Array of IDs (strings with auto-detection) */
  ids: string[];
  /** List of fields to include in the response. If not specified, all base publication fields are returned. Use 'providerData' to include provider-specific metadata. */
  fields?: AcademicSearchPublicationField[];
}

/**
 * Custom error class for PDF Vector API errors
 */
export class PDFVectorError extends Error {
  constructor(
    message: string,
    /** HTTP status code */
    public status?: number,
    /** Custom error code */
    public code?: string,
  ) {
    super(message);
    this.name = "PDFVectorError";
  }
}

/**
 * PDF Vector TypeScript/JavaScript SDK client
 *
 * @example
 * Parse from URL:
 * ```typescript
 * const client = new PDFVector({ apiKey: "pdfvector_xxx" });
 * const result = await client.parse({
 *   url: "https://example.com/document.pdf",
 *   useLLM: "auto"
 * });
 * console.log(result.markdown);
 * ```
 *
 * Parse from data:
 * ```typescript
 * import { readFile } from "fs/promises";
 *
 * const client = new PDFVector({ apiKey: "pdfvector_xxx" });
 * const result = await client.parse({
 *   data: await readFile("document.pdf"),
 *   contentType: "application/pdf",
 *   useLLM: "auto"
 * });
 * console.log(result.markdown);
 * ```
 *
 * Ask questions about documents:
 * ```typescript
 * const client = new PDFVector({ apiKey: "pdfvector_xxx" });
 * const result = await client.ask({
 *   url: "https://example.com/research-paper.pdf",
 *   prompt: "What are the main findings and methodology?"
 * });
 * console.log(result.markdown);
 * console.log(`Cost: ${result.creditCount} credits`);
 * ```
 *
 * Ask with JSON mode:
 * ```typescript
 * const result = await client.ask({
 *   url: "https://example.com/research-paper.pdf",
 *   prompt: "Extract the research data",
 *   mode: "json",
 *   schema: {
 *     type: "object",
 *     properties: {
 *       title: { type: "string" },
 *       methodology: { type: "string" },
 *       findings: { type: "array", items: { type: "string" } }
 *     }
 *   }
 * });
 * console.log(result.json);
 * ```
 *
 * Search academic publications:
 * ```typescript
 * const client = new PDFVector({ apiKey: "pdfvector_xxx" });
 * const results = await client.academicSearch({
 *   query: "machine learning transformers",
 *   providers: ["semantic-scholar", "arxiv"],
 *   limit: 10,
 *   yearFrom: 2020
 * });
 * console.log(results.results);
 * ```
 */
export class PDFVector {
  /** API key for authentication (format: pdfvector_xxx) */
  readonly apiKey: string;
  /** Base URL for the PDF Vector API. Defaults to https://www.pdfvector.com */
  readonly baseUrl: string;

  /**
   * Creates a new PDF Vector client
   * @param config Configuration object containing API key and optional base URL
   */
  constructor(config: PDFVectorConfig) {
    this.apiKey = config.apiKey;
    this.baseUrl = config.baseUrl ?? "https://www.pdfvector.com";
  }

  /**
   * Handle API response errors (internal)
   * @private
   */
  private async handleResponseError(response: Response): Promise<never> {
    const errorText = await response.text();
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    let errorCode: string | undefined;

    try {
      const errorData = JSON.parse(errorText) as {
        message?: string;
        code?: string;
      };
      if (errorData.message) errorMessage = errorData.message;
      if (errorData.code) errorCode = errorData.code;
    } catch {
      if (errorText) errorMessage = errorText;
    }

    throw new PDFVectorError(errorMessage, response.status, errorCode);
  }

  /**
   * Create a temporary upload URL for file upload (internal)
   * @private
   */
  private async createTemporaryUpload(
    contentType: string,
  ): Promise<TemporaryUploadResponse> {
    const url = `${this.baseUrl}/v1/api/temporary-upload`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${this.apiKey}`,
      },
      body: JSON.stringify({ contentType }),
    });

    if (!response.ok) await this.handleResponseError(response);

    const data = await response.json();
    return data as TemporaryUploadResponse;
  }

  /**
   * Parse a PDF/Word document from URL or data and convert to markdown
   * @param request Parse request parameters (URL or data)
   * @returns Promise resolving to parsed document details
   * @throws {PDFVectorError} When the API request fails
   */
  async parse(
    request: ParseURLRequest | ParseDataRequest,
  ): Promise<ParseResponse> {
    // Handle data input
    if ("data" in request) {
      // Create temporary upload
      const temporaryUpload = await this.createTemporaryUpload(
        request.contentType,
      );

      // Upload data using temporary upload URL
      const temporaryResponse = await fetch(temporaryUpload.url, {
        method: "PUT",
        body: request.data,
        headers: {
          "content-type": request.contentType,
        },
      });

      if (!temporaryResponse.ok) {
        throw new PDFVectorError(
          `Failed to upload file: HTTP ${temporaryResponse.status}`,
        );
      }

      // Convert to URL request for processing
      const urlRequest: ParseURLRequest = {
        url: `pdfvector-s3://${temporaryUpload.key}`,
        useLLM: request.useLLM,
      };

      return this.parse(urlRequest);
    }

    // Handle URL input
    const url = `${this.baseUrl}/v1/api/parse`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${this.apiKey}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) await this.handleResponseError(response);

    const data = await response.json();
    return data as ParseResponse;
  }

  /**
   * Ask questions about a PDF/Word document and get AI-powered answers
   * @param request Ask request parameters (URL or data with prompt)
   * @returns Promise resolving to AI-generated answer about the document
   * @throws {PDFVectorError} When the API request fails
   *
   * @example
   * Ask from URL (markdown mode):
   * ```typescript
   * const client = new PDFVector({ apiKey: "pdfvector_xxx" });
   * const result = await client.ask({
   *   url: "https://example.com/research-paper.pdf",
   *   prompt: "What are the key findings in this research?"
   * });
   * console.log(result.markdown);
   * console.log(`Cost: ${result.creditCount} credits (${result.pageCount} pages)`);
   * ```
   *
   * @example
   * Ask from data (JSON mode):
   * ```typescript
   * import { readFile } from "fs/promises";
   *
   * const client = new PDFVector({ apiKey: "pdfvector_xxx" });
   * const result = await client.ask({
   *   data: await readFile("document.pdf"),
   *   contentType: "application/pdf",
   *   prompt: "Extract key information from this document",
   *   mode: "json",
   *   schema: {
   *     type: "object",
   *     properties: {
   *       title: { type: "string" },
   *       authors: { type: "array", items: { type: "string" } },
   *       summary: { type: "string" },
   *       keyFindings: { type: "array", items: { type: "string" } }
   *     },
   *     required: ["title", "summary"]
   *   }
   * });
   * console.log(result.json);
   * ```
   */
  async ask(request: AskURLRequest | AskDataRequest): Promise<AskResponse> {
    // Handle data input
    if ("data" in request) {
      // Create temporary upload
      const temporaryUpload = await this.createTemporaryUpload(
        request.contentType,
      );

      // Upload data using temporary upload URL
      const temporaryResponse = await fetch(temporaryUpload.url, {
        method: "PUT",
        body: request.data,
        headers: {
          "content-type": request.contentType,
        },
      });

      if (!temporaryResponse.ok) {
        throw new PDFVectorError(
          `Failed to upload file: HTTP ${temporaryResponse.status}`,
        );
      }

      // Convert to URL request for processing
      const urlRequest: AskURLRequest = {
        url: `pdfvector-s3://${temporaryUpload.key}`,
        prompt: request.prompt,
        mode: request.mode,
        schema: request.schema,
      };

      return this.ask(urlRequest);
    }

    // Handle URL input
    const url = `${this.baseUrl}/v1/api/ask`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${this.apiKey}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) await this.handleResponseError(response);

    const data = await response.json();
    return data as AskResponse;
  }

  /**
   * Search academic publications across multiple databases
   * @param request Search request parameters
   * @returns Promise resolving to search results
   * @throws {PDFVectorError} When the API request fails
   *
   * @example
   * ```typescript
   * const results = await client.academicSearch({
   *   query: "machine learning",
   *   providers: ["semantic-scholar", "arxiv"],
   *   limit: 20,
   *   fields: ["title", "authors", "abstract", "year"]
   * });
   * ```
   */
  async academicSearch(
    request: SearchRequest,
  ): Promise<AcademicSearchResponse> {
    const url = `${this.baseUrl}/v1/api/academic-search`;

    // Prepare request body
    const body = {
      query: request.query,
      providers: request.providers?.length
        ? request.providers
        : ["semantic-scholar"],
      offset: request.offset ?? 0,
      limit: request.limit ?? 20,
      yearFrom: request.yearFrom,
      yearTo: request.yearTo,
      fields: request.fields,
    };

    // Remove undefined values from body
    const cleanBody = Object.fromEntries(
      Object.entries(body).filter(([, value]) => value !== undefined),
    );

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${this.apiKey}`,
      },
      body: JSON.stringify(cleanBody),
    });

    if (!response.ok) await this.handleResponseError(response);

    const data = await response.json();
    return data as AcademicSearchResponse;
  }

  /**
   * Search academic publications (alias for academicSearch)
   * @param request Search request parameters
   * @returns Promise resolving to search results
   * @throws {PDFVectorError} When the API request fails
   */
  async search(request: SearchRequest): Promise<AcademicSearchResponse> {
    return this.academicSearch(request);
  }

  /**
   * Fetch specific academic publications by their IDs with auto-detection
   * @param request Fetch request parameters containing IDs and optional fields
   * @returns Promise resolving to fetch results and errors
   * @throws {PDFVectorError} When the API request fails
   *
   * @example
   * Auto-detection with mixed ID formats:
   * ```typescript
   * const results = await client.academicFetch({
   *   ids: [
   *     "10.1038/nature12373",              // DOI auto-detected
   *     "12345678",                         // PubMed ID auto-detected
   *     "2301.00001",                       // ArXiv ID auto-detected
   *     "arXiv:2507.16298v1",               // ArXiv with prefix
   *     "ED123456",                         // ERIC ID auto-detected
   *     "0f40b1f08821e22e859c6050916cec3667778613", // Semantic Scholar
   *     "pubmed:98765432",                  // PDFVector format
   *     "some-unknown-id"                   // Will try all providers
   *   ]
   * });
   * ```
   *
   * @example
   * Fetching specific fields only:
   * ```typescript
   * const results = await client.academicFetch({
   *   ids: ["10.1038/nature12373", "pubmed:12345678"],
   *   fields: ["title", "authors", "year", "abstract"]
   * });
   * ```
   *
   * @example
   * Handling results:
   * ```typescript
   * // Successfully fetched publications
   * results.results.forEach(pub => {
   *   console.log(`Fetched: ${pub.title}`);
   *   console.log(`Provider: ${pub.detectedProvider}`);
   *   console.log(`Requested as: ${pub.id}`);
   * });
   *
   * // Handle errors for IDs that couldn't be fetched
   * results.errors?.forEach(error => {
   *   console.log(`Failed to fetch ${error.id}: ${error.error}`);
   *   if (error.code === "NOT_FOUND") {
   *     console.log("Publication not found in any provider");
   *   }
   * });
   * ```
   */
  async academicFetch(request: FetchRequest): Promise<AcademicFetchResponse> {
    const url = `${this.baseUrl}/v1/api/academic-fetch`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${this.apiKey}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) await this.handleResponseError(response);

    const data = await response.json();
    return data as AcademicFetchResponse;
  }

  /**
   * Fetch specific academic publications by their IDs (alias for academicFetch)
   * @param request Fetch request parameters containing IDs and optional fields
   * @returns Promise resolving to fetch results and errors
   * @throws {PDFVectorError} When the API request fails
   */
  async fetch(request: FetchRequest): Promise<AcademicFetchResponse> {
    return this.academicFetch(request);
  }
}
